<template lang="html">
	<common-card class="omapi-statswidget__graph">
		<apexchart
			type="area"
			width="100%"
			height="250px"
			:options="chartOptions"
			:series="[
				{
					name: '',
					data: dates.values,
				},
			]"
			class="leads-chart"
		/>
	</common-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { addDays, format, isAfter, isFuture, subDays } from 'date-fns';
import { dates } from '@/utils';

export default {
	computed: {
		...mapGetters('monsterleads', ['statsPeriod', 'statsDates', 'dateRange', 'startEnd']),
		numberDays() {
			const { start, end } = this.startEnd;
			if (start && end) {
				const diff = dates.timestamp(end) - dates.timestamp(start);
				return Math.ceil(diff / dates.DAY_IN_SECONDS);
			}

			return 0;
		},
		view() {
			let view = 'days';

			if (61 < this.numberDays) {
				view = 'months';
			}
			if (731 < this.numberDays) {
				view = 'years';
			}

			return view;
		},
		labelFormat() {
			return 'years' === this.view ? 'yyyy' : 'months' === this.view ? 'MMM.yyyy' : 'M/d';
		},
		tooltipFormat() {
			return 'years' === this.view ? 'yyyy' : 'months' === this.view ? 'MMMM yyyy' : 'M/d/yy';
		},
		dates() {
			const range = [];
			const labelsObj = {};

			let start = new Date(this.startEnd.start || Date.now());
			let end = new Date(this.startEnd.end || Date.now());

			if (1 > this.numberDays) {
				// Add one preceding day, for proper comparison.
				start = subDays(start, 1);

				// Attempt to add one following day as well, unless following
				// day is in the future.
				let newEnd = addDays(end, 1);
				if (!isFuture(newEnd)) {
					end = newEnd;
				}
			}

			while (!isAfter(start, end)) {
				const date = new Date(start);
				const dateString = format(start, 'yyyy-MM-dd');
				const count = parseInt(this.$get(`statsDates.${dateString}`, 0), 10);

				range.push(date);

				const label = this.getDateLabel(date);
				if (!labelsObj[label]) {
					labelsObj[label] = 0;
				}
				labelsObj[label] += count;

				// Increase day.
				start = addDays(start, 1);
			}

			const labels = Object.keys(labelsObj)
				// Remove the underscore added in getDateLabels.
				.map((d) => d.replace('_', '').split('.')[0]);

			return {
				values: Object.values(labelsObj),
				range,
				labels,
			};
		},
		chartOptions() {
			const tooltipStyles = {
				fontSize: '11px',
				cssClass: 'omapi-statswidget__graph-labels',
			};

			const { labels } = this.dates;
			const rotateLabels = 13 < labels.length;

			/**
			 * Override the default tooltip position to be centered above the point,
			 * instead of to the side.
			 *
			 * @param {object} chartContext The chart context provided by ApexCharts.
			 * @param {object} config The chart configuration object provided by ApexCharts.
			 *
			 * @returns {void}
			 */
			function overrideMoveTooltip(chartContext, config) {
				// Override the tooltip positioning to instead always be centered
				// above the dot.
				config.globals.tooltip.marker.tooltipPosition.moveTooltip = function moveTooltip(cx, cy, r = null) {
					let x = parseInt(cx) - this.ttCtx.tooltipRect.ttWidth;

					if (!isNaN(x)) {
						x += this.w.globals.translateX;

						const tooltipEl = this.ttCtx.getElTooltip();
						tooltipEl.style.left = x + 'px';

						const pointR = null !== r ? parseInt(r) : 1;
						const y = parseInt(cy) + pointR / 2; // - tooltipRect.ttHeight / 2
						tooltipEl.style.top = y + 'px';
					}
				};
			}

			return {
				colors: ['#46B450'],
				markers: {
					size: 4,
					hover: {
						size: 4,
					},
				},
				chart: {
					events: {
						mounted: overrideMoveTooltip,
						updated: overrideMoveTooltip,
					},
					type: 'area',
					zoom: {
						enabled: false,
					},
					toolbar: {
						show: false,
					},
					animations: {
						enabled: true,
						easing: 'easeout',
						speed: 1200,
						animateGradually: {
							enabled: true,
							delay: 50,
						},
					},
				},
				fill: {
					type: 'solid',
					opacity: 0.09,
				},
				dataLabels: {
					enabled: false,
				},
				stroke: {
					curve: 'straight',
					width: 2,
				},
				title: {
					show: false,
				},
				grid: {
					show: true,
					strokeDashArray: '1 4',
					borderColor: '#DDDDDD',
					yaxis: {
						lines: {
							show: true,
						},
					},
					xaxis: {
						lines: {
							show: false,
						},
					},
				},
				xaxis: {
					categories: labels,
					labels: {
						show: true,
						minHeight: 35,
						trim: false,
						rotateAlways: rotateLabels,
						style: tooltipStyles,
					},
					tooltip: {
						enabled: false,
					},
					axisBorder: {
						show: false,
					},
					axisTicks: {
						show: false,
					},
				},
				yaxis: {
					type: 'numeric',
					labels: {
						show: true,
						formatter: (val) => {
							return val ? this.$numbers.withCommas(val.toFixed(0)) : 0;
						},
						style: tooltipStyles,
					},
					tickAmount: this.getTickAmount(8),
				},
				legend: {
					show: false,
				},
				tooltip: {
					followCursor: false,
					custom: ({ series, dataPointIndex: i }) => {
						let html = '';
						const val = series[0][i];
						if (val || 0 === val) {
							html += `
							<div class="omapi-has-tooltip omapi-has-tooltip__no-icon">
								<div class="apexcharts-tooltip-series-group popper">
									<span><strong>${this.$numbers.withCommas(val)}</strong> - ${this.tooltip(i)}</span>
									<div x-arrow="" class="popper__arrow"></div>
								</div>
							</div>
							`;
						}

						return html;
					},
				},
			};
		},
	},
	methods: {
		getTickAmount(defaultAmount) {
			let amount = 0;

			this.dates.values.forEach((d) => {
				amount = Math.max(parseInt(d, 10), amount);
			});

			return Math.min(defaultAmount, amount);
		},
		getDateLabel(date) {
			let label = format(date, this.labelFormat);

			if ('Jan' === label.split('.')[0] || '1/1' === label) {
				// Only output (new) year.
				// Add an underscore to keep it from sorting.
				label = '_' + format(date, 'yyyy');
			}

			return label;
		},
		tooltip(index) {
			return format(this.dates.range[index], this.tooltipFormat);
		},
	},
};
</script>

<style lang="scss">
@import '../../../assets/scss/variables';
.omapi-statswidget__graph {
	.omapi-card-content {
		padding: 1px 5px !important;
	}
	&-labels {
		fill: $font_secondary-color !important;
	}

	.apexcharts-tooltip {
		overflow: visible;
		border: 0;
		background: transparent;
		box-shadow: none;
		// margin-left: 80px !important;
		// margin-top: -17px !important;
		transform: translate(50%, -50%);

		// Debugging
		// opacity: 1 !important;

		&-series-group {
			width: auto;
			box-sizing: border-box;

			// display: flex;
			justify-content: center;

			position: relative;
			background-color: $tooltip_background-color;
			border-color: $tooltip_background-color;
			border-radius: $tooltip_border-radius;
			padding: 0;
			padding: $tooltip_padding;
			color: $tooltip_color;
			text-align: center;
			box-shadow: none;
			font-size: 13px;
			font-weight: 300;
			display: flex;
			&.active,
			&:last-child {
				margin: 0 !important;
				padding: $tooltip_padding;
			}
		}

		&-y-group {
			padding: 0;
		}
		&-text-value {
			font-weight: normal;
			margin: 0;
			line-height: 1em;
		}
		&-count-value {
			font-weight: bold;
		}
	}
}
.omapi-screen .omapi-statswidget__graph .omapi-has-tooltip .popper .popper__arrow {
	// border-color: $color_black transparent transparent transparent;
	// border-width: 6px;
	bottom: -18px;
	left: 50%;
	margin-left: -6px;
}

// Debugging
// .apexcharts-tooltip-series-group.popper:after {
//     content: '';
//     background: red;
//     width: 1px;
//     height: 78px;
//     position: absolute;
//     left: 50%;
// }
</style>
